import { useTranslation } from 'react-i18next';
import { Droplet, Trash2, Container, Truck, Package, MoreHorizontal } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import type { TripType, RateUnit } from '@/lib/types';

interface TripTypeSelectorProps {
  value: TripType;
  onChange: (next: TripType) => void;
  disabled?: boolean;
}

const TRIP_TYPES: { value: TripType; icon: typeof Droplet; color: string; defaultRateUnit: RateUnit }[] = [
  { value: 'water',     icon: Droplet,         color: 'text-cyan-500',    defaultRateUnit: 'per_m3' },
  { value: 'sewage',    icon: Trash2,          color: 'text-amber-700',   defaultRateUnit: 'per_m3' },
  { value: 'container', icon: Container,       color: 'text-blue-600',    defaultRateUnit: 'per_trip' },
  { value: 'flatbed',   icon: Truck,           color: 'text-emerald-600', defaultRateUnit: 'per_trip' },
  { value: 'pickup',    icon: Package,         color: 'text-orange-500',  defaultRateUnit: 'per_trip' },
  { value: 'other',     icon: MoreHorizontal,  color: 'text-muted-foreground', defaultRateUnit: 'per_trip' },
];

export function TripTypeSelector({ value, onChange, disabled }: TripTypeSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label>{t('contracts.field.tripType')} <span className="text-destructive">*</span></Label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {TRIP_TYPES.map((tt) => {
          const Icon = tt.icon;
          const selected = value === tt.value;
          return (
            <button
              key={tt.value}
              type="button"
              onClick={() => onChange(tt.value)}
              disabled={disabled}
              className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-md border text-sm font-medium transition-all ${
                selected
                  ? 'border-gold bg-gold/10 text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
              }`}
            >
              <Icon className={`h-5 w-5 ${selected ? tt.color : 'text-muted-foreground'}`} />
              <span className="text-xs">{t(`contracts.tripType.${tt.value}`)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function getDefaultRateUnitForTripType(tt: TripType): RateUnit {
  const found = TRIP_TYPES.find((t) => t.value === tt);
  return found?.defaultRateUnit ?? 'per_trip';
}

interface RateUnitSelectorProps {
  value: RateUnit;
  onChange: (next: RateUnit) => void;
  disabled?: boolean;
}

const RATE_UNITS: { value: RateUnit; helper: string }[] = [
  { value: 'per_m3',   helper: 'volume' },
  { value: 'per_trip', helper: 'fixed' },
  { value: 'per_ton',  helper: 'weight' },
  { value: 'per_km',   helper: 'distance' },
];

export function RateUnitSelector({ value, onChange, disabled }: RateUnitSelectorProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <Label>{t('contracts.field.rateUnit')} <span className="text-destructive">*</span></Label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {RATE_UNITS.map((ru) => {
          const selected = value === ru.value;
          return (
            <button
              key={ru.value}
              type="button"
              onClick={() => onChange(ru.value)}
              disabled={disabled}
              className={`p-2.5 rounded-md border text-sm transition-all ${
                selected
                  ? 'border-gold bg-gold/10 text-foreground font-medium'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
              }`}
            >
              <div className="text-sm">{t(`contracts.rateUnit.${ru.value}`)}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{t(`contracts.rateUnitHelper.${ru.helper}`)}</div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {value === 'per_trip'
          ? t('contracts.help.perTripExplain')
          : t('contracts.help.perVolumeExplain')}
      </p>
    </div>
  );
}
